import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react'

interface ActiveComponentContextValue {
  /** The unit component whose tab is active, once tabs have initialized. */
  activeComponentId: string | undefined
  setActiveComponentId: (componentId: string) => void
}

const ActiveComponentContext =
  createContext<ActiveComponentContextValue | null>(null)

/**
 * Owns which unit component tab is active for the ticket SESSION, not per
 * step mount. Mounted in `LibraViewContent` ABOVE the step-keyed
 * `TicketStepContextProvider`, so navigating between steps (which remounts
 * the whole step subtree, including `UnitComponentTabs`) no longer snaps
 * the user back to the first component. Resets naturally on ticket change
 * because the page keys on `ticket.id`.
 */
export const ActiveComponentProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [activeComponentId, setActiveComponentId] = useState<
    string | undefined
  >(undefined)
  const value = useMemo(
    () => ({ activeComponentId, setActiveComponentId }),
    [activeComponentId]
  )
  return (
    <ActiveComponentContext.Provider value={value}>
      {children}
    </ActiveComponentContext.Provider>
  )
}

export function useActiveComponent(): ActiveComponentContextValue {
  const ctx = useContext(ActiveComponentContext)
  if (!ctx) {
    throw new Error(
      'useActiveComponent must be used within an ActiveComponentProvider'
    )
  }
  return ctx
}
